import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { FormData } from '../models/ModelExpenses';

export function useExpensesImport() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    fileType: '',
    fileFormat: '',
    bank: '',
    files: [],
  });
  const navigate = useNavigate();
  const totalSteps = 5;
  const [isLoading, setIsLoading] = useState(false);

  const handleOptionSelect = (option: string, field: 'fileType' | 'fileFormat' | 'bank') => {
    setFormData({ ...formData, [field]: option });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      for (const file of selectedFiles) {
        if (file.size > maxSize) {
          alert(`File ${file.name} exceeds 10MB limit`);
          return;
        }
        if (formData.fileFormat === 'xlsx' && !file.name.endsWith('.xlsx')) {
          alert(`Please select an XLSX file for ${file.name}`);
          return;
        }
        if (formData.fileFormat === 'pdf' && !file.name.endsWith('.pdf')) {
          alert(`Please select a PDF file for ${file.name}`);
          return;
        }
      }
      setFormData({ ...formData, files: selectedFiles });
    }
  };

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const restartSelection = () => {
    setCurrentStep(1);
    setFormData({ fileType: '', fileFormat: '', bank: '', files: [] });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (formData.files.length === 0) return;

    setIsLoading(true);
    try {
      const csrfToken = await getCsrfToken();

      // Helper to read a file as base64
      const readFileAsBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve(base64String);
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });

      const base64Files = await Promise.all(
        formData.files.map((file) => readFileAsBase64(file))
      );

      const response = await fetch('/api/expenses/import/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          file_type: formData.fileType,
          file_format: formData.fileFormat,
          bank_name: formData.bank,
          files: base64Files, // sending an array of files
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to import expenses');
      }

      const data = await response.json();
      if (data.success) {
        navigate('/expenses/preview');
      }

    } catch (error) {
      console.error('Error importing expenses:', error);
      alert(
        `Error importing expenses: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    currentStep,
    totalSteps,
    formData,
    handleOptionSelect,
    handleFileSelect,
    handleNextStep,
    handlePrevStep,
    restartSelection,
    handleSubmit,
    navigate,
    isLoading,
  };
}